<script setup>
// apps/Evento/Publico/ProximasPeleas.vue
//
// Pantalla pública sala de espera — 3 tatamis en columnas.
// Tema claro: fondo blanco, texto negro, rojo y azul para peleadores.
// Fix de imágenes: usa ref reactivo para detectar error y mostrar iniciales.

import { ref, onMounted, onUnmounted } from "vue";
import { loadSlim } from "tsparticles-slim";
import heroImage from "@/assets/img/Evolve/img_1.jpg";
import { connectPublico, closePublico } from "@/utils/ws_peticiones_evento";

// ── Partículas ──────────────────────────────────────────────────────────────
const particleConfig = {
  background: { color: "transparent" },
  particles: {
    number: { value: 35 },
    color: { value: "#00aaff" },
    shape: { type: "circle" },
    opacity: { value: 0.2 },
    size: { value: 2.5 },
    move: { enable: true, speed: 1.0, direction: "none", outModes: "out" },
  },
};

const particlesInit = async (engine) => {
  await loadSlim(engine);
};

// ── Estado ─────────────────────────────────────────────────────────────────
const tatamis = ref({
  1: { peleas: [], estado: null },
  2: { peleas: [], estado: null },
  3: { peleas: [], estado: null },
});

const hora = ref("");
let clockTimer = null;

// ── Lifecycle ──────────────────────────────────────────────────────────────
onMounted(() => {
  connectPublico({ onMessage: manejar });
  actualizarHora();
  clockTimer = setInterval(actualizarHora, 1000);
});

onUnmounted(() => {
  closePublico();
  clearInterval(clockTimer);
});

// ── WebSocket ──────────────────────────────────────────────────────────────
function manejar(msg) {
  if (msg.type === "estado_inicial") {
    const porTatami = {};
    (msg.data?.tatamis || []).forEach((t) => {
      porTatami[t.tatami_id] = t.peleas || [];
    });
    [1, 2, 3].forEach((tid) => {
      const lista = porTatami[tid] || [];
      tatamis.value[tid].peleas = filtrarPeleas(lista);
      tatamis.value[tid].estado = estadoActualTatami(lista);
    });
    return;
  }

  if (msg.type === "marcador_update") {
    const tid = Number(msg.tatami_id);
    if (!tatamis.value[tid]) return;

    const lista = tatamis.value[tid].peleas;
    const idx = lista.findIndex((p) => String(p.pelea_id) === String(msg.pelea_id));
    const pelea = {
      pelea_id: msg.pelea_id,
      ronda: msg.ronda,
      estado: msg.estado_pelea,
      categoria: msg.categoria || null,
      rojo: {
        nombre: msg.rojo?.nombre || null,
        equipo: msg.rojo?.equipo || null,
        foto: msg.rojo?.foto || null,
        fotoError: false,
      },
      azul: {
        nombre: msg.azul?.nombre || null,
        equipo: msg.azul?.equipo || null,
        foto: msg.azul?.foto || null,
        fotoError: false,
      },
    };

    if (idx >= 0) {
      lista[idx] = pelea;
    } else {
      lista.push(pelea);
    }

    tatamis.value[tid].peleas = filtrarPeleas(lista);
    tatamis.value[tid].estado = estadoActualTatami(lista);
  }
}

// ── Helpers ────────────────────────────────────────────────────────────────
const ESTADOS_VISIBLES = ["en_curso", "pausada", "pendiente", "tiempo_agotado"];
const ORDEN_ESTADO = { en_curso: 0, pausada: 1, tiempo_agotado: 2, pendiente: 3 };

function filtrarPeleas(lista) {
  return lista
    .filter((p) => ESTADOS_VISIBLES.includes(p.estado))
    .sort((a, b) => {
      const oa = ORDEN_ESTADO[a.estado] ?? 9;
      const ob = ORDEN_ESTADO[b.estado] ?? 9;
      if (oa !== ob) return oa - ob;
      if (a.ronda !== b.ronda) return a.ronda - b.ronda;
      return (a.numero_pelea || 0) - (b.numero_pelea || 0);
    });
}

function estadoActualTatami(lista) {
  if (lista.some((p) => p.estado === "en_curso")) return "en_curso";
  if (lista.some((p) => p.estado === "tiempo_agotado")) return "tiempo_agotado";
  if (lista.some((p) => p.estado === "pausada")) return "pausada";
  if (lista.some((p) => p.estado === "pendiente")) return "pendiente";
  return null;
}

function esActiva(p) {
  return p.estado === "en_curso" || p.estado === "tiempo_agotado";
}
function esSiguiente(lista, p) {
  if (esActiva(p)) return false;
  return lista.findIndex((x) => x.estado === "pendiente") === lista.indexOf(p);
}

function iniciales(nombre) {
  if (!nombre) return "?";
  return nombre
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

// Fix imágenes: marca el flag reactivo en la pelea correspondiente
function onFotoError(pelea, lado) {
  if (pelea[lado]) {
    pelea[lado].fotoError = true;
  }
}

const ESTADO_LABEL = {
  en_curso: "EN CURSO",
  pausada: "PAUSADA",
  pendiente: "PENDIENTE",
  tiempo_agotado: "T. AGOTADO",
  finalizada: "FINALIZADA",
};

function actualizarHora() {
  const d = new Date();
  hora.value = [d.getHours(), d.getMinutes(), d.getSeconds()]
    .map((n) => String(n).padStart(2, "0"))
    .join(":");
}
</script>

<template>
  <div class="root">
    <!-- ── Capas de fondo ── -->
    <div class="hero-bg-image" :style="{ backgroundImage: `url(${heroImage})` }"></div>
    <div class="hero-gradient"></div>
    <Particles id="peleasParticles" :init="particlesInit" :options="particleConfig" />
    <div class="smoke-layer"></div>

    <!-- ── Contenido ── -->
    <div class="content">
      <div class="grid">
        <div v-for="tid in [1, 2, 3]" :key="tid" class="col">
          <!-- Cabecera del tatami -->
          <div class="col-header">
            <span class="tatami-label">TATAMI {{ tid }}</span>
            <span
              v-if="tatamis[tid].estado"
              class="estado-chip"
              :class="tatamis[tid].estado"
            >
              {{ ESTADO_LABEL[tatamis[tid].estado] || tatamis[tid].estado }}
            </span>
          </div>

          <!-- Lista de peleas -->
          <div class="pelea-lista">
            <div
              v-for="p in tatamis[tid].peleas"
              :key="p.pelea_id"
              class="pelea-card"
              :class="{
                activa: esActiva(p),
                siguiente: esSiguiente(tatamis[tid].peleas, p),
                pausada: p.estado === 'pausada',
                agotado: p.estado === 'tiempo_agotado',
              }"
            >
              <!-- Fondo diagonal rojo/azul -->
              <div class="card-bg">
                <div class="half-rojo"></div>
                <div class="half-azul"></div>
              </div>

              <!-- Fila de peleadores -->
              <div class="fighters-row">
                <!-- ROJO -->
                <div class="fighter rojo-side">
                  <div class="photo-wrap">
                    <img
                      v-if="p.rojo?.foto && !p.rojo.fotoError"
                      :src="p.rojo.foto"
                      :alt="p.rojo.nombre"
                      class="photo-img"
                      @error="onFotoError(p, 'rojo')"
                    />
                    <div v-else class="photo-iniciales rojo-bg">
                      {{ iniciales(p.rojo?.nombre) }}
                    </div>
                  </div>
                  <div class="fighter-info">
                    <div class="fighter-name">{{ p.rojo?.nombre || "BYE" }}</div>
                    <div class="fighter-team">{{ p.rojo?.equipo || "—" }}</div>
                  </div>
                </div>

                <!-- VS -->
                <div class="vs-col">
                  <span class="live-badge" v-if="esActiva(p)">● EN VIVO</span>
                  <span v-else class="estado-mini" :class="p.estado">
                    {{
                      esSiguiente(tatamis[tid].peleas, p)
                        ? "SIGUIENTE"
                        : ESTADO_LABEL[p.estado]
                    }}
                  </span>
                  <span class="vs-text">VS</span>
                  <span class="ronda-badge">Ronda {{ p.ronda }}</span>
                </div>

                <!-- AZUL -->
                <!-- AZUL -->
                <div class="fighter azul-side">
                  <div class="fighter-info right">
                    <div class="fighter-name">{{ p.azul?.nombre || "BYE" }}</div>
                    <div class="fighter-team">{{ p.azul?.equipo || "—" }}</div>
                  </div>
                  <div class="photo-wrap">
                    <img
                      v-if="p.azul?.foto && !p.azul.fotoError"
                      :src="p.azul.foto"
                      :alt="p.azul.nombre"
                      class="photo-img"
                      @error="onFotoError(p, 'azul')"
                    />
                    <div v-else class="photo-iniciales azul-bg">
                      {{ iniciales(p.azul?.nombre) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Sin peleas -->
            <div v-if="tatamis[tid].peleas.length === 0" class="sin-pelea">
              <span class="sin-icon">⚔</span>
              <span>Sin combates activos</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Oswald:wght@400;600;700&family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;600;700&display=swap");

*,
*::before,
*::after {
  box-sizing: border-box;
}

.root {
  position: relative;
  min-height: 100vh;
  font-family: "Oswald", sans-serif;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.hero-bg-image {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: brightness(0.7) contrast(1.15);
  z-index: 1;
}

.hero-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.88) 0%,
    rgba(0, 15, 35, 0.72) 50%,
    rgba(0, 0, 0, 0.92) 100%
  );
  z-index: 2;
}

#peleasParticles {
  position: absolute;
  inset: 0;
  z-index: 3;
}

.smoke-layer {
  position: absolute;
  inset: 0;
  background: url("https://c4.wallpaperflare.com/wallpaper/448/699/737/abstract-digital-art-3d-abstract-lines-wallpaper-preview.jpg");
  opacity: 0.18;
  mix-blend-mode: lighten;
  animation: smokeMove 20s infinite linear;
  z-index: 4;
}

@keyframes smokeMove {
  0% {
    transform: translateY(0) scale(1.2);
  }

  100% {
    transform: translateY(-20%) scale(1.25);
  }
}

.content {
  position: relative;
  z-index: 20;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* ── Grid 3 columnas ── */
.grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.col {
  display: flex;
  flex-direction: column;
  border-right: 1px solid rgba(0, 0, 0, 0.08);
  overflow: hidden;
  background: transparent;
}

.col:last-child {
  border-right: none;
}

/* ── Cabecera columna ── */
.col-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 16px 20px;
  flex-shrink: 0;
  background: #0d131c;
}

.tatami-label {
  font-family: "Oswald", sans-serif;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 5px;
  color: #ffffff;
  line-height: 1;
  text-transform: uppercase;
}

/* ── Estado chips (cabecera tatami) ── */
.estado-chip {
  font-family: "DM Mono", monospace;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  padding: 4px 10px;
  border-radius: 20px;
  border: 1.5px solid rgba(255, 255, 255, 0.4);
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
}

.estado-chip.en_curso {
  color: #86efac;
  background: rgba(34, 197, 94, 0.2);
  border-color: #86efac;
}

.estado-chip.pausada {
  color: #fde68a;
  background: rgba(245, 158, 11, 0.2);
  border-color: #fde68a;
}

.estado-chip.pendiente {
  color: #bfdbfe;
  background: rgba(59, 130, 246, 0.2);
  border-color: #bfdbfe;
}

.estado-chip.tiempo_agotado {
  color: #fca5a5;
  background: rgba(239, 68, 68, 0.2);
  border-color: #fca5a5;
}

/* ── Lista de peleas ── */
.pelea-lista {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  gap: 8px;
  overflow-y: auto;
  scrollbar-width: none;
}

.pelea-lista::-webkit-scrollbar {
  display: none;
}

/* ── Pelea card ── */
.pelea-card {
  border-radius: 10px;
  border: 1.5px solid #e2e4e8;
  overflow: hidden;
  position: relative;
  height: 90px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.pelea-card.activa {
  border-color: #22c55e;
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.15);
}

.pelea-card.siguiente {
  border-color: #e8b800;
  box-shadow: 0 0 0 2px rgba(232, 184, 0, 0.12);
}

.pelea-card.pausada {
  border-color: #f59e0b;
}

.pelea-card.agotado {
  border-color: #ef4444;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.12);
}

/* ── Fondo diagonal ── */
.card-bg {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.half-rojo {
  position: absolute;
  inset: 0;
  background: #ffdbdb;
  clip-path: polygon(0 0, 55% 0, 45% 100%, 0 100%);
}

.half-azul {
  position: absolute;
  inset: 0;
  background: #e1eeff;
  clip-path: polygon(55% 0, 100% 0, 100% 100%, 45% 100%);
}

/* ── Fila top: ronda + estado — centrados ── */
.card-top {
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 22px;
  flex-shrink: 0;
}

.ronda-badge {
  font-family: "DM Mono", monospace;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #4b5563;
}

.live-badge {
  font-family: "DM Mono", monospace;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #15803d;
  border: 1.5px solid #22c55e;
  border-radius: 10px;
  padding: 2px 8px;
  background: #dcfce7;
  animation: pulse-live 1.5s infinite;
}

@keyframes pulse-live {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }
}

.estado-mini {
  font-family: "DM Mono", monospace;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1px;
  padding: 2px 8px;
  border-radius: 10px;
  border: 1.5px solid currentColor;
}

.estado-mini.pendiente {
  color: #1d4ed8;
  background: #dbeafe;
}

.estado-mini.pausada {
  color: #b45309;
  background: #fef3c7;
}

.estado-mini.tiempo_agotado {
  color: #b91c1c;
  background: #fee2e2;
}

/* ── Fila peleadores ── */
.fighters-row {
  position: relative;
  z-index: 5;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: stretch;
  flex: 1;
  min-height: 0;
}

.fighter.rojo-side {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 6px 6px 6px 8px;
  min-width: 0;
}

.fighter.azul-side {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 6px 8px 6px 6px;
  min-width: 0;
}

/* ── Foto rectangular ── */
.photo-wrap {
  width: 44px;
  height: 70px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
  background: #e2e8f0;
}

.photo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  display: block;
}

.photo-iniciales {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "DM Sans", sans-serif;
  font-size: 14px;
  font-weight: 700;
}

.rojo-bg {
  background: #fee2e2;
  color: #b91c1c;
}

.azul-bg {
  background: #dbeafe;
  color: #1d4ed8;
}

/* ── Info del peleador ── */
.fighter-info {
  min-width: 0;
  flex: 1;
}

.fighter-info.right {
  text-align: right;
}

.fighter-name {
  font-family: "Oswald", sans-serif;
  font-size: clamp(11px, 1vw, 14px);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}

.fighter-team {
  font-family: "DM Sans", sans-serif;
  font-size: clamp(9px, 0.75vw, 10px);
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
}

/* ── VS ── */
.vs-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  gap: 2px;
}

.vs-text {
  font-family: "Bebas Neue", sans-serif;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #000000;
}

/* ── Sin peleas ── */
.sin-pelea {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #aaa;
  font-family: "DM Sans", sans-serif;
  font-size: 13px;
  letter-spacing: 1px;
  padding: 40px 0;
}

.sin-icon {
  font-size: 28px;
  opacity: 0.4;
}
</style>
